/**
 * fire_system.ts — every fire in the world, drawn one way (fire realism R1–R4,
 * R12, 2026-09-25). Owners REGISTER a fire; the system owns everything a fire
 * is made of:
 *
 *   · the FLAME on its quality rung (lib/vfx/fire_rules.ts `fireRungFor`):
 *     the procedural box march ('volume'), two cylindrical cards ('cards'),
 *     the blackbody sprites ('sprite'), the simulated grid ('sim', ultra);
 *   · the FUEL BED — the ring's coals glowing through their cracks;
 *   · the LIGHT — a SOURCE on the shared `LightBroker` (fires hold 3 lights on
 *     high/ultra, 2 on low/med, 1 below), its intensity scaled by the night
 *     (no daylight floor) and pulsing with the flame's own FLICKER;
 *   · EMBERS and SMOKE on the shared emitters (lib/vfx/fire_particles.ts), or
 *     the sprite tier's CPU embers and the WebGL 2 smoke cards;
 *   · the POPS — a seeded Poisson stream: a crackle transient, an ember burst
 *     and an 80 ms light spike from one seed;
 *   · a CHAR mark on the ground (an `impact_decals` 'char'), HEAT HAZE over the
 *     flames at high+, and the positional crackle loop of the nearest fire.
 *
 * TWO WAYS TO REGISTER. `add(key, spec)` / `remove(key)` for an owner that
 * tracks its fires (the town fixtures, the stages); `anchor(spec)` returns an
 * empty Object3D the owner adds to ITS OWN group — the fire lives exactly as
 * long as that anchor is attached to the scene (settlement dressing, outposts,
 * the preset fire lounge: their owners build and tear down groups without
 * knowing fires exist). Positions are ABSOLUTE (scene children carry absolute
 * coordinates; an anchor's render-space world position minus `scene.position`).
 *
 * Transients: `fireball()` (a ground explosion's fireball that cools into
 * smoke) and fires with `lifeS` (the after-fire at a wreck).
 *
 * ON THE CURVE (session 3, 2026-09-26 — the lighting agent's physical-emissive
 * contract). Each frame, from lib/photometry's `exposureState` / `lightFrame`
 * (LightingModule, RenderSub 4 — before this tick): the fire's RADIANCE SCALE
 * (fire_rules `fireRadianceScale` → `uFireEmission`, the sprite colours, the
 * ember ramp, the smoke's under-glow), the pool LIGHT (`fireLightScene`:
 * candela × luxToScene) and the soot's light — the sun and the sky in scene
 * units ((key · 0.3 + sky) / π, the six-way column: key / π, sky / π).
 * `physical = false` (the probe's A/B lever) restores the pre-curve fixed units.
 */
import * as THREE from 'three/webgpu';
import type { App } from 'enginex';
import { createHeatHazeMaterial } from 'enginex/vfx/HeatHazeMaterial.js';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import {
  Flicker, PopStream, popRateFor, fireRungFor, fireLightScale, fireLightRange, fireBox, fireballAt,
  FIRE_KIND, FIRE_DRAW_M, FIRE_STEPS, FLICKER_EXPOSURE, FLICKER_LIGHT, FLICKER_HEIGHT, FLICKER_JITTER_M, POP_SPIKE, POP_SPIKE_S,
  FIRE_LIGHT_FIXED_CD, FIRE_CORE_REF, fireRadianceScale, fireCoreScene, fireLightScene, fireHeldStops,
  type FireKind, type FireQuality, type FireRung,
} from './fire_rules.js';
// surfaces (2026-09-27): §4.3 the transient emitters on the curve
import { EMITTER_NITS, EMITTER_DISPLAY_MAX, FIRE_CORE_NITS, emitterScene, emitterLightScene } from './fire_rules.js';
import { uBlastEmission, BLAST_RING_REF, uFireBlue } from './fire_volume.js';
import { setPlumeEmission, PLUME_REF } from './plume_material.js';
import { FIRE_HAZE_MAX, FIRE_HAZE_M, fireHazeK } from './fire_rules.js'; // surfaces (2026-09-27): §4.4
// fire realism (2026-10-08): a kind's own flame, the puff, the Froude clock
import { FIRE_SHAPE, firePuffHz, fireTimeScale, PUFF_JITTER, PUFF_LIGHT, LIGHT_FOLLOW, LIGHT_FOLLOW_TAU_S, LIGHT_FOLLOW_RANGE, EMBER_RATE, SMOKE_RATE, type FireShape } from './fire_rules.js';
import { hazeSceneColor } from './fire_volume.js';
import { uniform } from 'three/tsl';
/** §4.4: the haze quad's per-object strength (userData.hazeK — the fire's heat output). */
const uHazeK = uniform(1).onObjectUpdate(((frame: { object: THREE.Object3D | null }) => (frame.object?.userData.hazeK as number | undefined) ?? 1) as never);
import { exposureState, lightFrame } from '../photometry.js';
import {
  fireVolumeMaterial, fireballMaterial, fireBoxGeometry, fireCardGeometry, fireCardMaterial, fireSpriteMaterials, stepFireFlipbook, fireFlipbookMaterials, FIRE_FLIP, FIRE_SHADER, flameEmissionCpu, fireSmokeCardGeometry,
  fireSmokeCardMaterial, fireBedMaterial, uSmokeLight, uFireEmission, setFireSpriteScale, bakeFireNoise, fireTextures, fireSimMaterial,
} from './fire_volume.js';
import { FireSim } from './fire_sim.js';
import { blackbodyHex, blackbodyLut } from './blackbody.js';
import { LightBroker } from './light_broker.js';
import { createFireParticles, emitEmbers, emitSmoke, updateFireParticles, setSmokeGlow, setSmokeLight, setEmberScale, type FireParticles } from './fire_particles.js';
import { fireBedGeometry } from '../station_props.js';
import { impactDecals, impactDecalsIfAny } from '../impact_decals.js';
import { sixWayLightFor } from '../stage5_fx.js';
import { sfxAt, sfxLoop, stopSfxScope, sfxAvailable } from '../sfx.js';
import type { LoopHandle } from '../audio/runtime.js';

export interface FireSpec {
  /** ABSOLUTE base of the flame (the top of the logs / the bowl / the torch head). */
  x: number; y: number; z: number;
  kind?: FireKind;
  /** 1 = a campfire (a 1.3 × 2.3 m box); defaults by kind. */
  size?: number;
  seed?: number;
  /** Hold a pool light (default true). */
  light?: boolean;
  smoke?: boolean;
  embers?: boolean;
  /** A char mark on the ground under it (default by kind). */
  char?: boolean;
  /** The coal bed (the fire ring's cylinder) under the flame. */
  bed?: boolean;
  /** A crackle-loop candidate (default true for campfires and bonfires). */
  sound?: boolean;
  /** The ground under the fire when the flame's base is raised (the char mark and the coal bed sit there). */
  groundY?: number;
  /** …or the same as a drop below the flame's base (anchors: their owner knows the offset, not the absolute). */
  groundDrop?: number;
  /** Seconds until the fire burns out and is removed (after-fires). */
  lifeS?: number;
  /** false = SMOKE ONLY (an explosion's lingering column): no flame, no light, no embers (default true). */
  flame?: boolean;
}

interface SpriteFlames { flames: THREE.Sprite[]; flick: Flicker[]; embers: Array<{ s: THREE.Sprite; t: number; life: number; vx: number; vz: number; v0: number }> }

interface Fire {
  key: string;
  x: number; y: number; z: number;
  groundY: number;
  kind: FireKind;
  size: number;
  seed: number;
  heat: number;
  light: boolean; smoke: boolean; embers: boolean; char: boolean; bed: boolean; sound: boolean; flame: boolean;
  /**
   * Seconds this fire has burned on the FIRE clock (the frame step — 0 while `__fire().freeze()` holds it) — a lifetime
   * is measured on it, not on the wall (session 3: the WebGL2 probe's slow captures outlived a frozen blast's smoke).
   */
  age: number;
  lifeS: number;
  group: THREE.Group;
  vol: THREE.Mesh | null;
  simMesh: THREE.Mesh | null;
  cards: THREE.Mesh | null;
  sprites: SpriteFlames | null;
  smokeCards: THREE.Mesh | null;
  bedMesh: THREE.Mesh | null;
  haze: THREE.Mesh | null;
  charMesh: THREE.Mesh | null;
  rung: FireRung | null;
  flicker: Flicker;
  pops: PopStream;
  spike: number;
  A: THREE.Vector4; B: THREE.Vector4; C: THREE.Vector4;
  /** The kind's flame (fire_rules FIRE_SHAPE), its shape uniforms (fireD) and the smoke cards' own B (wind bend, density). */
  shape: FireShape; D: THREE.Vector4; SB: THREE.Vector4;
  /** THE PUFF's phase (cycles): advanced at the fire's own shedding frequency; the envelope's bulge and the light ride it. */
  puff: number;
  /** The drawn flame's emission, running mean (THE LIGHT FOLLOWS THE FLAME); 0 = not measured yet. */
  emMean: number;
  /** …and the latest ratio (emission ÷ its mean, clamped) — the probe lever's `drawn`. */
  emRatio: number;
  emberAcc: number;
  smokeAcc: number;
  dist: number;
  t: number;
  lightOn: boolean;
  /** An anchor's owner has hidden its group (warm-then-show, a tier cull): the fire waits, dark. */
  suppressed: boolean;
}

interface Fireball { mesh: THREE.Mesh; age: number; life: number; scale: number; x: number; y: number; z: number; A: THREE.Vector4; B: THREE.Vector4 }

interface AnchorRec { obj: THREE.Object3D; spec: FireSpec; key: string; live: boolean; detachedAt: number; lastPos: THREE.Vector3; createdAt: number }

export interface FireEnv {
  camera: THREE.Camera | null;
  /** The camera in ABSOLUTE coordinates. */
  cam: { x: number; y: number; z: number };
  night: number;
  sunDir: THREE.Vector3;
  /** The weather wind: unit direction (x, z) and strength (≈ 0..1.5). */
  wind: { x: number; z: number; s: number };
  fogDensity: number;
  quality: FireQuality;
  /** The tier's particle cap (0..1). */
  particleCap: number;
  /** Draw heat haze (high+ with the VFX pack, and the backend allows it). */
  haze: boolean;
  /** Steps of the volume march on this tier. */
  steps: number;
  inWorld: boolean;
}

const _v = new THREE.Vector3();
const _sp = new THREE.Vector3();
const LIGHT_HEX = blackbodyHex(1900); // the light a wood fire casts on skin (~1,900 K effective)
const FIRE_GLOW_RGB = [1.0, 0.42, 0.12] as const;
/** The six-way smoke column's glow from the fire under it, against the flame's radiance scale (see `update`). */
const SMOKE_UNDER_GLOW = 0.1;

export class FireSystem {
  readonly root = new THREE.Group();
  private readonly fires = new Map<string, Fire>();
  private readonly anchors = new Map<THREE.Object3D, AnchorRec>();
  private readonly fireballs: Fireball[] = [];
  private readonly ballPool: THREE.Mesh[] = [];
  private particles: FireParticles | null = null;
  /** The ultra rung's shared compute grid (WebGPU; built the first time a fire reaches the 'sim' rung). */
  private sim: FireSim | null = null;
  private hazeMat: { material: THREE.NodeMaterial; uniforms: { strength: { value: number } } } | null = null;
  private crackle: LoopHandle | null = null;
  private anchorScanAt = 0;
  private charScanAt = 0;
  /** This frame's heat-haze gate (high+ · the VFX pack · a backend that takes the scene copy) — the explosions read it. */
  hazeOn = false;
  /** Probe levers (the black-frame evidence legs): hide every fire, force the heat haze off. Never set in play. */
  debugHide = false;
  debugNoHaze = false;
  /** Probe lever: pin the haze's distortion strength (null = the tier's 0.006). 0 keeps the quad AND its scene copy. */
  debugHazeStrength: number | null = null;
  /**
   * ON THE CURVE (session 3): true = the flame, coals, embers, smoke glow and pool light follow lib/photometry (the
   * default, always in play); false = the pre-curve fixed units — the probe's A/B lever (`__fire().physical(false)`).
   */
  physical = true;
  /** This frame's curve (the `__fire()` diagnostics and the probe): the radiance scale, the core's display value, the light. */
  readonly curve = { k: 1, coreScene: 0, coreDisplay: 0, heldStops: 0, light1: 0, smoke: [0, 0, 0] as [number, number, number] };
  /** Frames the system has ticked (the probe samples the flicker on DISTINCT frames). */
  private frame = 0;
  private lutF: Float32Array | null = null;
  private readonly _sun: [number, number, number] = [0, 1, 0];
  private readonly _lc: [number, number, number] = [0, 0, 0];
  private readonly _amb: [number, number, number] = [0, 0, 0];
  private seq = 0;
  readonly stats = { added: 0, removed: 0, anchors: 0, pops: 0, fireballs: 0, rung: { sprite: 0, cards: 0, volume: 0, sim: 0 } as Record<FireRung, number>, lights: 0, visible: 0, hazes: 0 };

  constructor(private readonly app: App, private readonly broker: LightBroker | null) {
    this.root.name = 'fire-root';
    app.world.scene.add(this.root);
    fireTextures();
    void bakeFireNoise();
  }

  /** The shared ember/smoke emitters (built on first need so the tier at that moment decides). */
  private ensureParticles(env: FireEnv, litSmoke: boolean): FireParticles | null {
    if (this.particles || env.quality === 'sprite') return this.particles;
    this.particles = createFireParticles(this.app, { particleCap: env.particleCap, litSmoke });
    return this.particles;
  }

  // ── Registration ─────────────────────────────────────────────────────────
  add(key: string, spec: FireSpec): void {
    const had = this.fires.get(key);
    if (had) {
      // Same key, moved: re-seat (a camp relocated, an anchor's group sank).
      if (Math.hypot(had.x - spec.x, had.y - spec.y, had.z - spec.z) > 0.05) { had.x = spec.x; had.y = spec.y; had.z = spec.z; had.groundY = spec.groundY ?? spec.y - (spec.groundDrop ?? 0); had.group.position.set(spec.x, spec.y, spec.z); this.placeChar(had, true); }
      return;
    }
    const kind = spec.kind ?? 'campfire';
    const def = FIRE_KIND[kind];
    const seed = spec.seed ?? hashKey(key);
    const size = spec.size ?? def.size;
    const shape = FIRE_SHAPE[kind];
    const group = new THREE.Group();
    group.name = `fire:${key}`;
    group.position.set(spec.x, spec.y, spec.z);
    this.root.add(group);
    const f: Fire = {
      key, x: spec.x, y: spec.y, z: spec.z, groundY: spec.groundY ?? spec.y - (spec.groundDrop ?? 0), kind, size, seed, heat: def.heat,
      light: spec.light ?? true, smoke: spec.smoke ?? def.smoke, embers: spec.embers ?? def.embers, char: spec.char ?? def.char,
      bed: spec.bed ?? false, sound: (spec.flame ?? true) && (spec.sound ?? (kind === 'campfire' || kind === 'bonfire' || kind === 'hearth')), flame: spec.flame ?? true,
      age: 0, lifeS: spec.lifeS ?? 0,
      group, vol: null, simMesh: null, cards: null, sprites: null, smokeCards: null, bedMesh: null, haze: null, charMesh: null, rung: null,
      flicker: new Flicker(seed), pops: new PopStream(seed, popRateFor(seed, size)), spike: 0,
      A: new THREE.Vector4(fract01(seed * 0.000137), shape.gain, def.heat, 0), B: new THREE.Vector4(0, 0, shape.flameH, shape.soot), C: new THREE.Vector4(0, shape.baseR, 0, shape.puff),
      shape, D: new THREE.Vector4(shape.tongues, shape.rag, shape.pinch, shape.sway), SB: new THREE.Vector4(0, 0, 0, 1), puff: fract01(seed * 0.0131), emMean: 0, emRatio: 1,
      emberAcc: 0, smokeAcc: 0, dist: Infinity, t: fract01(seed * 0.00071) * 50, lightOn: false, suppressed: false,
    };
    if (f.bed) {
      const bed = new THREE.Mesh(fireBedGeometry(), fireBedMaterial());
      bed.name = 'fire-bed';
      bed.position.y = f.groundY - f.y; // the ring's coals sit on the ground, not at the flame base
      bed.receiveShadow = true;
      bed.userData.fireA = f.A;
      bed.userData.sharedAsset = true;
      group.add(bed);
      f.bedMesh = bed;
    }
    if (!f.flame) { f.light = false; f.embers = false; f.char = false; f.heat = 0.25; f.A.z = 0.25; }
    this.fires.set(key, f);
    this.placeChar(f, false);
    this.stats.added++;
  }

  remove(key: string): void {
    const f = this.fires.get(key);
    if (!f) return;
    this.fires.delete(key);
    this.root.remove(f.group);
    // Every mesh here shares its geometry and material (the GPU shield law: nothing drawn is destroyed).
    if (f.charMesh && f.charMesh.userData.fireKey === f.key) impactDecalsIfAny()?.release(f.charMesh);
    this.stats.removed++;
  }

  has(key: string): boolean { return this.fires.has(key); }

  /**
   * An ANCHOR: add the returned Object3D to your own group; the fire burns
   * while it is attached to the scene and goes out when it is not.
   */
  anchor(spec: Omit<FireSpec, 'x' | 'y' | 'z'> & { x?: number; y?: number; z?: number }): THREE.Object3D {
    const o = new THREE.Object3D();
    o.name = 'fire-anchor';
    if (spec.x !== undefined) o.position.set(spec.x, spec.y ?? 0, spec.z ?? 0);
    const rec: AnchorRec = { obj: o, spec: { x: 0, y: 0, z: 0, ...spec } as FireSpec, key: `anchor:${++this.seq}`, live: false, detachedAt: 0, lastPos: new THREE.Vector3(Infinity, 0, 0), createdAt: performance.now() };
    this.anchors.set(o, rec);
    this.stats.anchors = this.anchors.size;
    return o;
  }

  private scanAnchors(now: number): void {
    const scene = this.app.world.scene;
    for (const [o, a] of this.anchors) {
      let top: THREE.Object3D = o;
      let shown = o.visible;
      while (top.parent) { top = top.parent; if (!top.visible && top !== scene) shown = false; }
      const attached = top === scene;
      const live = this.fires.get(a.key);
      if (live) live.suppressed = !shown;
      if (attached) {
        o.updateWorldMatrix(true, false);
        o.getWorldPosition(_v).sub(scene.position); // render space → ABSOLUTE
        a.detachedAt = 0;
        if (!a.live || _v.distanceToSquared(a.lastPos) > 0.0025) {
          a.lastPos.copy(_v);
          this.add(a.key, { ...a.spec, x: _v.x, y: _v.y, z: _v.z, groundY: undefined });
          a.live = true;
        }
      } else if (a.live) {
        if (!a.detachedAt) a.detachedAt = now;
        if (now - a.detachedAt > 1000) { this.remove(a.key); a.live = false; }
      } else if (!a.detachedAt) a.detachedAt = now;
      // Forget an anchor that has been off the scene a minute (its owner dropped it) or never arrived in two.
      if (a.detachedAt && now - a.detachedAt > 60000) this.anchors.delete(o);
      else if (!a.live && now - a.createdAt > 120000 && !attached) this.anchors.delete(o);
    }
    this.stats.anchors = this.anchors.size;
  }

  /** A ground explosion's FIREBALL at an ABSOLUTE point: expands, cools, turns to smoke (lib/vfx/fire_rules `fireballAt`). */
  fireball(x: number, y: number, z: number, scale: number, lifeS = 1.1): void {
    if (this.fireballs.length >= 10) return;
    let mesh = this.ballPool.pop();
    if (!mesh) {
      mesh = new THREE.Mesh(fireBoxGeometry(), fireballMaterial(14));
      mesh.name = 'fireball';
      mesh.frustumCulled = false;
      mesh.renderOrder = 13;
    }
    const A = new THREE.Vector4(Math.random() * 7, 1, 1, 0), B = new THREE.Vector4(0, 0, 0.35, 0);
    mesh.userData.fireA = A; mesh.userData.fireB = B;
    mesh.position.set(x, y, z);
    mesh.scale.setScalar(2.6 * scale);
    mesh.visible = true;
    this.root.add(mesh);
    this.fireballs.push({ mesh, age: 0, life: lifeS, scale, x, y, z, A, B });
    this.stats.fireballs++;
  }

  // ── Explosion services (lib/vfx/explosion_fx.ts, CombatVfx) ──────────────
  /** A transient light from the shared pool (an explosion's flash, a firework break): true if a slot was taken. */
  flash(req: { x: number; y: number; z: number; color: number; intensity: number; rangeM: number; ms: number; maxRangeM?: number; onCurve?: { candela: number; nits: number; displayMax: number } }, viewer: { x: number; y: number; z: number }): boolean {
    // §4.3 (surfaces, 2026-09-27): a flash ON THE CURVE carries its candela and its look's luminance — the light is
    // candela × luxToScene cut by its held stops (fire_rules `emitterLightScene`); `intensity` is the pre-curve fixed
    // value (the A/B lever `__fire().physical(false)`, and callers that never moved).
    const c = req.onCurve;
    const intensity = c && this.physical && lightFrame.valid ? emitterLightScene(c.candela, c.nits, c.displayMax, exposureState) : req.intensity;
    return this.broker?.flash({ x: req.x, y: req.y, z: req.z, color: req.color, intensity, rangeM: req.rangeM, ms: req.ms, maxRangeM: req.maxRangeM, cat: 'flash' }, viewer) ?? false;
  }
  /** `n` embers at an ABSOLUTE point (a debris chunk's trail, a spark shower). */
  sparks(x: number, y: number, z: number, n: number): void {
    if (this.particles?.embers) emitEmbers(this.particles, this.app.world.scene, x, y, z, n, 0, 0, 0);
  }
  /** The blast's scorch on the ground (the impact decals' 'blast' kind). */
  scorch(x: number, groundY: number, z: number, scale: number): void {
    impactDecals(this.app.world.scene).place('blast', _v.set(x, groundY, z), _sp.set(0, 1, 0), { sizeMul: Math.max(0.6, scale) });
  }

  // ── The char mark ────────────────────────────────────────────────────────
  private placeChar(f: Fire, force: boolean): void {
    if (!f.char) return;
    const d = impactDecals(this.app.world.scene);
    if (!force && f.charMesh && f.charMesh.userData.fireKey === f.key && f.charMesh.visible && f.charMesh.parent) return;
    const m = d.place('char', _v.set(f.x, f.groundY, f.z), _sp.set(0, 1, 0), { sizeMul: Math.max(0.5, f.size), lifeS: 1e9 });
    m.userData.fireKey = f.key;
    f.charMesh = m;
  }

  // ── Visuals per rung ─────────────────────────────────────────────────────
  private ensureRung(f: Fire, r: FireRung, env: FireEnv): void {
    const box = fireBox(f.size);
    if (r === 'sim' && !f.simMesh) {
      if (!this.sim) this.sim = new FireSim(this.app.renderer.native as never);
      const m = new THREE.Mesh(fireBoxGeometry(), fireSimMaterial(FIRE_STEPS.sim, this.sim.dyeRead));
      m.name = 'fire-sim';
      m.position.y = box.h / 2;
      m.scale.set(box.w, box.h, box.w);
      m.renderOrder = 9;
      m.userData.fireA = f.A; m.userData.fireB = f.B; m.userData.fireC = f.C; m.userData.fireD = f.D;
      f.group.add(m);
      f.simMesh = m;
    }
    if (r === 'volume' && !f.vol) {
      const m = new THREE.Mesh(fireBoxGeometry(), fireVolumeMaterial(env.steps));
      m.name = 'fire-volume';
      m.position.y = box.h / 2;
      m.scale.set(box.w, box.h, box.w);
      m.frustumCulled = true;
      m.renderOrder = 9; // before the player-following light box and the smoke, after the opaques
      m.userData.fireA = f.A; m.userData.fireB = f.B; m.userData.fireC = f.C; m.userData.fireD = f.D;
      f.group.add(m);
      f.vol = m;
    }
    if (r === 'cards' && !f.cards) {
      const m = new THREE.Mesh(fireCardGeometry(), fireCardMaterial());
      m.name = 'fire-cards';
      m.scale.set(box.w, box.h, box.w); // the cards are slices of the WHOLE box (the volume's own flame and plume)
      m.renderOrder = 9;
      m.userData.fireA = f.A; m.userData.fireB = f.B; m.userData.fireC = f.C; m.userData.fireD = f.D;
      f.group.add(m);
      f.cards = m;
    }
    if (r === 'sprite' && !f.sprites) {
      const mats = fireSpriteMaterials();
      const sp: SpriteFlames = { flames: [], flick: [], embers: [] };
      for (let i = 0; i < 3; i++) {
        const s = new THREE.Sprite(mats.flame);
        s.userData.sharedAsset = true;
        s.position.set((i - 1) * 0.42 * f.shape.baseR * box.w, 0.3 * f.size, (i % 2) * 0.1 * f.size - 0.05 * f.size);
        s.renderOrder = 2;
        f.group.add(s);
        sp.flames.push(s);
        sp.flick.push(new Flicker(f.seed + 17 * (i + 1)));
      }
      if (f.embers) for (let i = 0; i < 7; i++) {
        const s = new THREE.Sprite(mats.ember);
        s.userData.sharedAsset = true;
        s.scale.setScalar(0.05);
        f.group.add(s);
        sp.embers.push({ s, t: Math.random() * 2, life: 1.5 + Math.random() * 1.5, vx: (Math.random() - 0.5) * 0.25, vz: (Math.random() - 0.5) * 0.25, v0: 1.1 + Math.random() * 0.6 });
      }
      f.sprites = sp;
    }
  }

  private showRung(f: Fire, r: FireRung | null): void {
    if (f.vol) f.vol.visible = r === 'volume';
    if (f.simMesh) f.simMesh.visible = r === 'sim';
    if (f.cards) f.cards.visible = r === 'cards';
    if (f.sprites) { const on = r === 'sprite'; for (const s of f.sprites.flames) s.visible = on; for (const e of f.sprites.embers) e.s.visible = on; }
  }

  // ── The frame ────────────────────────────────────────────────────────────
  update(dt: number, env: FireEnv, opts: { litSmoke: boolean; webgpu: boolean }): void {
    const now = performance.now();
    if (!env.inWorld) {
      if (this.root.visible) { this.root.visible = false; this.crackle?.set(0, 1, 0.3); }
      return;
    }
    this.root.visible = !this.debugHide;
    this.hazeOn = env.haze && !this.debugNoHaze;
    if (now - this.anchorScanAt > 500) { this.anchorScanAt = now; this.scanAnchors(now); }
    // A char mark evicted by a firefight's decals comes back (the decal pool evicts its oldest).
    if (now - this.charScanAt > 2000) { this.charScanAt = now; for (const f of this.fires.values()) if (f.char && f.dist < 120) this.placeChar(f, false); }
    // The shared emitters are built the first time a smoking / ember-throwing fire is near (never at world entry:
    // a scene with no fire in reach carries no emitter — and `__findMaterial()` can census the scene before them).
    let fp = this.particles;
    if (!fp) for (const f of this.fires.values()) if ((f.embers || f.smoke) && Math.hypot(f.x - env.cam.x, f.z - env.cam.z) < 70) { fp = this.ensureParticles(env, opts.litSmoke); break; }
    this.frame++;
    // ON THE CURVE (session 3): the radiance scale, the pool light at size 1 and the soot's light, once a frame.
    const onCurve = this.physical && lightFrame.valid;
    const k = onCurve ? fireRadianceScale(exposureState) : 1;
    const light1 = onCurve ? fireLightScene(1, exposureState) : FIRE_LIGHT_FIXED_CD * fireLightScale(env.night);
    (uFireEmission as unknown as { value: number }).value = k;
    setFireSpriteScale(k);
    // §4.3 (surfaces, 2026-09-27): the blast's flash front and the (opt-in physical) plumes, on the same curve.
    (uBlastEmission as unknown as { value: number }).value = onCurve ? emitterScene(EMITTER_NITS.blast, EMITTER_DISPLAY_MAX.blast, exposureState) / BLAST_RING_REF : 1;
    setPlumeEmission(onCurve ? emitterScene(EMITTER_NITS.plume, EMITTER_DISPLAY_MAX.plume, exposureState) / PLUME_REF : 1);
    // §4.5 (surfaces, 2026-09-27): the blue base at night only (the dark-adapted eye), on the curve.
    (uFireBlue as unknown as { value: number }).value = onCurve ? Math.min(1, Math.max(0, env.night)) : 0;
    const sl = uSmokeLight.value as THREE.Vector3;
    if (onCurve) {
      // Rule 3: lit by the sun and the sky in scene units — the soot's in-scatter ≈ (key · 0.3 + sky) / π, the six-way
      // column's light colour (the albedo's multiplier) key / π and its ambient sky / π.
      const ks = lightFrame.keyScene, sk = lightFrame.skyScene;
      sl.set((ks.x * 0.3 + sk.x) / Math.PI, (ks.y * 0.3 + sk.y) / Math.PI, (ks.z * 0.3 + sk.z) / Math.PI);
      const sunUp = ks.x + ks.y + ks.z > 1e-6 && env.sunDir.y > -0.02;
      this._sun[0] = sunUp ? env.sunDir.x : 0; this._sun[1] = sunUp ? env.sunDir.y : 1; this._sun[2] = sunUp ? env.sunDir.z : 0;
      this._lc[0] = ks.x / Math.PI; this._lc[1] = ks.y / Math.PI; this._lc[2] = ks.z / Math.PI;
      this._amb[0] = sk.x / Math.PI; this._amb[1] = sk.y / Math.PI; this._amb[2] = sk.z / Math.PI;
    } else {
      // The pre-curve soot light (the six-way smoke's fixed-unit light: the sun by day, a faint zenith sky by night).
      const L = sixWayLightFor([env.sunDir.x, env.sunDir.y, env.sunDir.z], env.night);
      sl.set(L.ambient[0] + L.color[0] * 0.3, L.ambient[1] + L.color[1] * 0.3, L.ambient[2] + L.color[2] * 0.3);
      this._sun[0] = L.dir[0]; this._sun[1] = L.dir[1]; this._sun[2] = L.dir[2];
      this._lc[0] = L.color[0]; this._lc[1] = L.color[1]; this._lc[2] = L.color[2];
      this._amb[0] = L.ambient[0]; this._amb[1] = L.ambient[1]; this._amb[2] = L.ambient[2];
    }
    const cv = this.curve;
    cv.k = k; cv.light1 = light1; cv.smoke[0] = sl.x; cv.smoke[1] = sl.y; cv.smoke[2] = sl.z;
    cv.coreScene = onCurve ? fireCoreScene(exposureState) : FIRE_CORE_REF; cv.coreDisplay = cv.coreScene * (exposureState.exposure || 0.85);
    cv.heldStops = onCurve ? fireHeldStops(exposureState) : 0;
    const leanK = Math.min(0.25, 0.18 * env.wind.s);
    const leanX = env.wind.x * leanK, leanZ = env.wind.z * leanK;
    // The smoke's under-glow accumulates the near fires' size (× the night on the pre-curve path; × k below on the curve).
    const glowNight = onCurve ? 1 : fireLightScale(env.night);
    // Rank by distance (the sim rung's heroes, the crackle loop's owner).
    const ranked: Fire[] = [];
    for (const f of this.fires.values()) {
      f.dist = Math.hypot(f.x - env.cam.x, f.y - env.cam.y, f.z - env.cam.z);
      f.age += dt;
      if (f.lifeS > 0 && f.age > f.lifeS) { this.remove(f.key); continue; }
      ranked.push(f);
    }
    ranked.sort((a, b) => a.dist - b.dist);
    const rungCount: Record<FireRung, number> = { sprite: 0, cards: 0, volume: 0, sim: 0 };
    let glow = 0, visible = 0, hazes = 0;
    for (let i = 0; i < ranked.length; i++) {
      const f = ranked[i];
      const vis = f.dist <= FIRE_DRAW_M && !f.suppressed;
      f.group.visible = vis;
      if (!vis) { f.rung = null; continue; }
      visible++;
      // THE FROUDE CLOCK: a larger fire's flame moves slower in its own box (buoyant speeds go as √size).
      f.t += dt * fireTimeScale(f.size);
      const s = f.flicker.step(dt);
      // THE PUFF: the fire's own shedding frequency (1.5 / √D Hz), its period wandering with the flicker.
      f.puff = fract01(f.puff + dt * firePuffHz(f.kind, f.size) * (1 + PUFF_JITTER * s));
      const swell = 1 + PUFF_LIGHT * f.shape.puff * Math.sin(6.2831853 * f.puff + 0.5);
      // Pops (near fires only): a crackle, a burst of embers, an 80 ms light spike — one seed.
      let burst = 0;
      if (f.dist < 32) {
        const n = f.pops.step(dt);
        if (n > 0) {
          f.spike = 1;
          burst = f.pops.burst() * n;
          this.stats.pops += n;
          if (f.dist < 22 && sfxAvailable('sfx_fire_pop')) sfxAt('sfx_fire_pop', { x: f.x, y: f.y + 0.3, z: f.z }, { gain: Math.min(0.6, 0.35 * f.size) * Math.max(0, 1 - f.dist / 22), rate: 0.85 + fract01(f.seed * 0.37 + f.t) * 0.35, cooldownMs: 60, caption: 'Fire crackles' });
        }
      }
      f.spike = Math.max(0, f.spike - dt / POP_SPIKE_S);
      // The rung and its uniforms (a smoke-only column has no flame: it draws its smoke on the 'cards' path).
      const rung = f.flame ? fireRungFor(env.quality, f.dist, opts.webgpu, i, f.rung) : env.quality === 'sprite' ? 'sprite' : 'cards';
      if (f.flame && rung !== f.rung) { this.ensureRung(f, rung, env); this.showRung(f, rung); }
      f.rung = rung;
      if (f.flame) rungCount[rung]++;
      const fogT = Math.exp(-((env.fogDensity * f.dist) ** 2));
      f.A.set(f.A.x, fogT * f.shape.gain * (1 + FLICKER_EXPOSURE * s) * (1 + POP_SPIKE * f.spike), f.heat, f.t);
      f.B.set(leanX, leanZ, f.shape.flameH * (1 + FLICKER_HEIGHT * s), f.shape.soot);
      f.C.set(rung === 'cards' ? Math.atan2(env.cam.x - f.x, env.cam.z - f.z) : 0, f.shape.baseR, f.puff, f.shape.puff);
      if (rung === 'sprite' && f.sprites) this.animateSprites(f, dt, s);
      // Light: a SOURCE on the broker, re-submitted every frame (the broker parks it the frame it stops).
      if (f.light && this.broker && f.dist < 90) {
        // …and it SWELLS with the puff: the light on the ground breathes with the flame that casts it.
        // THE LIGHT FOLLOWS THE FLAME: the drawn flame's own emission (six short oracle rays) over its running mean; the
        // puff's swell stands in until the noise volume is baked.
        let follow = swell;
        const ft = fireTextures();
        if (ft.noiseReady) {
          this.lutF ??= blackbodyLut();
          const est = flameEmissionCpu(ft.noiseData, this.lutF, [f.A.x, 1, f.A.z, f.A.w], [f.B.x, f.B.y, f.B.z, 0], [0, f.C.y, f.C.z, f.C.w], [f.D.x, f.D.y, f.D.z, f.D.w]);
          f.emMean = f.emMean > 0 ? f.emMean + (est - f.emMean) * (1 - Math.exp(-dt / LIGHT_FOLLOW_TAU_S)) : Math.max(est, 1e-3);
          f.emRatio = Math.min(LIGHT_FOLLOW_RANGE[1], Math.max(LIGHT_FOLLOW_RANGE[0], est / Math.max(f.emMean, 1e-3)));
          follow = 1 + LIGHT_FOLLOW * (f.emRatio - 1);
        }
        const I = light1 * f.size * f.size * (1 + FLICKER_LIGHT * s) * (1 + POP_SPIKE * f.spike) * follow;
        const jx = FLICKER_JITTER_M * s, jz = FLICKER_JITTER_M * f.flicker.value * 0.7;
        this.broker.source(`fire:${f.key}`, { cat: 'fire', x: f.x + jx, y: f.y + 0.45 * fireBox(f.size).h * f.shape.flameH + jx * 0.5, z: f.z + jz, color: LIGHT_HEX, intensity: I, rangeM: fireLightRange(f.size), maxRangeM: 30 });
      }
      // Embers (R3): a steady trickle + the pops' bursts, into the shared emitter.
      if (fp?.embers && f.embers && f.dist < 70 && rung !== 'sprite') {
        f.emberAcc += dt * EMBER_RATE * f.size * Math.max(0.3, env.particleCap);
        const n = Math.floor(f.emberAcc) + burst;
        if (n > 0) { f.emberAcc -= Math.floor(f.emberAcc); emitEmbers(fp, this.app.world.scene, f.x, f.y + 0.15 * f.size, f.z, n, env.wind.x, env.wind.z, leanK * 1.6); }
      }
      // Smoke (R4): the six-way column on WebGPU med+, the smoke cards elsewhere.
      if (f.smoke && rung !== 'sprite') {
        // Smoke leaves from the flame's TIPS (the kind's own flame height), never from inside it.
        const top = f.flame ? fireBox(f.size).h * f.shape.flameH * 0.85 : 0.5 * f.size;
        // A burning-out fire (an after-fire, a lingering blast column) smokes less as it dies.
        const dying = f.lifeS > 0 ? Math.max(0.15, 1 - f.age / f.lifeS) : 1;
        if (fp?.smoke && f.dist < 140) {
          f.smokeAcc += dt * SMOKE_RATE * f.size * dying;
          const n = Math.floor(f.smokeAcc);
          if (n > 0) { f.smokeAcc -= n; emitSmoke(fp, this.app.world.scene, f.x, f.y + top, f.z, n, env.wind.x, env.wind.z, leanK * 2.2, Math.max(0.5, f.size * f.shape.baseR / 0.31)); }
          if (f.smokeCards) f.smokeCards.visible = false;
        } else {
          if (!f.smokeCards) {
            const m = new THREE.Mesh(fireSmokeCardGeometry(), fireSmokeCardMaterial());
            m.name = 'fire-smoke-cards';
            m.position.y = top * 0.8;
            m.scale.set(1.5 * f.size, 5 * f.size, 1.5 * f.size);
            m.renderOrder = 10;
            m.userData.fireA = f.A; m.userData.fireB = f.SB; m.userData.fireC = f.C; // its own B: the flame's B.w is the kind's soot
            f.group.add(m);
            f.smokeCards = m;
          }
          f.smokeCards.visible = f.dist < 160;
          f.C.x = Math.atan2(env.cam.x - f.x, env.cam.z - f.z);
          // The smoke card reads B.x as its wind bend and B.w as its density.
          f.SB.set(leanX, leanZ, 0, 1);
        }
      }
      // Heat haze (high+): a camera-facing quad 0.5–2.5 m over the flames.
      // §4.4 (surfaces, 2026-09-27): the NEAREST FIRE_HAZE_MAX fires only (ranked is nearest-first), strength by heat.
      if (this.hazeOn && f.dist < FIRE_HAZE_M && f.kind !== 'torch' && hazes < FIRE_HAZE_MAX) {
        if (!f.haze) f.haze = this.makeHaze(f);
        if (f.haze) { hazes++; f.haze.userData.hazeK = fireHazeK(f.size); f.haze.visible = true; env.camera?.getWorldPosition(_v); if (env.camera) f.haze.lookAt(_v); }
      } else if (f.haze) f.haze.visible = false;
      if (f.dist < 40) glow += fogT * f.size * glowNight;
    }
    this.stats.rung = rungCount;
    this.stats.hazes = hazes; // §4.4 (surfaces, 2026-09-27)
    // The shared grid steps only while a hero fire is on the 'sim' rung (30 Hz, breathing with that fire's flicker).
    if (this.sim) {
      const hero = ranked.find((f) => f.rung === 'sim');
      this.sim.update(dt, hero ? hero.A.y : 0, env.wind.x * env.wind.s * 1.2, env.wind.z * env.wind.s * 1.2);
    }
    this.stats.visible = visible;
    if (this.hazeMat) this.hazeMat.uniforms.strength.value = this.debugHazeStrength ?? 0.006;
    // The under-glow of the smoke column follows the fires that feed it (× the radiance scale: the fire's own light);
    // its lit side is the sun and the sky (the six-way column is not on the shared fixed-unit updater — fire_particles).
    if (fp?.smoke) {
      // (fire realism, 2026-10-08) × SMOKE_UNDER_GLOW: measured, the air over a night fire is ~1 % of the flame's
      // luminance — the old column glowed at up to half of the core's and read as a second, grey flame.
      const g = Math.min(2.2, 0.6 + glow) * k * SMOKE_UNDER_GLOW;
      setSmokeGlow(fp, FIRE_GLOW_RGB[0] * g, FIRE_GLOW_RGB[1] * g, FIRE_GLOW_RGB[2] * g);
      setSmokeLight(fp, this._sun, this._lc, this._amb);
    }
    if (fp) { setEmberScale(fp, k); updateFireParticles(fp, dt, env.camera); }
    this.updateFireballs(dt);
    this.updateCrackle(ranked);
    if (this.broker) { this.broker.update(dt * 1000, env.cam); this.stats.lights = this.broker.usedBy('fire'); }
  }

  private animateSprites(f: Fire, dt: number, s: number): void {
    const sp = f.sprites!;
    // THE FLIPBOOK (fire realism): once baked, the sprite rung plays the volume rung's own flame — two layers of the
    // same loop half a loop apart (the second smaller), sized to the kind's envelope and standing on the fire's base.
    const flipMats = stepFireFlipbook(3) ? fireFlipbookMaterials() : null;
    if (flipMats) {
      const box = fireBox(f.size), N = FIRE_FLIP.frames;
      const w = FIRE_FLIP.spanX * box.w * (f.shape.baseR / FIRE_SHADER.baseRadius), h = FIRE_FLIP.spanY * box.h * (f.shape.flameH / FIRE_SHADER.flameHeight) * (1 + 0.5 * FLICKER_HEIGHT * s);
      const frame0 = f.t * FIRE_FLIP.fps + (f.seed % 97);
      for (let i = 0; i < sp.flames.length; i++) {
        const fl = sp.flames[i];
        if (i > 1) { fl.visible = false; continue; }
        const ls = i === 0 ? 1 : 0.78;
        fl.material = flipMats[Math.floor(frame0 + i * N * 0.5) % N];
        fl.scale.set(w * ls, h * ls, 1);
        // seated by POSITION, never `sprite.center`: three's sprite node binds `center` to the first sprite object a
        // shared program was built for (an ember's) — every flame hung half below its fire.
        fl.position.set(i === 0 ? 0 : 0.06 * box.w, h * ls * 0.5, i === 0 ? 0 : 0.05 * box.w);
      }
    } else for (let i = 0; i < sp.flames.length; i++) {
      const k = 0.8 + 0.22 * sp.flick[i].step(dt) + 0.12 * s;
      const fl = sp.flames[i];
      // sized to the kind's own flame (its envelope: 2·baseR of the box wide, flameH of it tall), the middle one tallest
      const box = fireBox(f.size);
      const fw = 2 * f.shape.baseR * box.w * 0.62, fh = f.shape.flameH * box.h * (i === 1 ? 0.92 : 0.7);
      fl.scale.set(fw * (0.85 + 0.2 * k), fh * (0.9 + 0.2 * s) * (0.8 + 0.25 * k), 1);
      fl.position.y = fl.scale.y * 0.47;
    }
    // Embers, dt-correct: buoyant rise that decays through drag, a sway, a shrink.
    for (const e of sp.embers) {
      e.t += dt;
      if (e.t > e.life) { e.t = 0; e.vx = (Math.random() - 0.5) * 0.25; e.vz = (Math.random() - 0.5) * 0.25; e.v0 = 1.1 + Math.random() * 0.6; }
      const u = e.t / e.life;
      const rise = (e.v0 / 0.8) * (1 - Math.exp(-0.8 * e.t)) + 0.3 * e.t;
      e.s.position.set((e.vx * e.t + Math.sin(f.t * 2 + e.life) * 0.05) * f.size, (0.35 + rise) * f.size, e.vz * e.t * f.size);
      e.s.scale.setScalar(0.06 * (1 - u) + 0.01);
    }
  }

  private makeHaze(f: Fire): THREE.Mesh | null {
    try {
      if (!this.hazeMat) {
        // THE HAZE COPY (surfaces, 2026-09-27): the format-safe scene copy (fire_volume hazeSceneColor) + the per-fire strength.
        const h = createHeatHazeMaterial({ strength: 0.006, noiseScale: 4, speed: 1.6, edgeSoftness: 0.55, sceneColor: hazeSceneColor() as never, strengthNode: uHazeK });
        markMrtOverlay(h.material);
        h.material.name = 'fire-heat-haze';
        this.hazeMat = h as never;
      }
      const q = new THREE.Mesh(new THREE.PlaneGeometry(1.4 * f.size, 2.0 * f.size), this.hazeMat!.material);
      q.name = 'fire-haze';
      q.position.y = 1.5 * f.size;
      q.renderOrder = 16;
      f.group.add(q);
      return q;
    } catch { return null; }
  }

  private updateFireballs(dt: number): void {
    for (let i = this.fireballs.length - 1; i >= 0; i--) {
      const b = this.fireballs[i];
      b.age += dt;
      const st = fireballAt(b.age, b.life);
      // §4.3 (surfaces, 2026-09-27): the fireball's core is 8 kcd/m² against the campfire core's 10 (the same held curve).
      b.A.set(b.A.x, this.physical ? EMITTER_NITS.fireball / FIRE_CORE_NITS : 1, st.heat, b.age);
      b.B.set(0, 0, st.radius, st.soot);
      b.mesh.position.y = b.y + 1.4 * b.age * b.scale; // the hot gas rises
      if (b.age > b.life * 1.9) {
        b.mesh.visible = false;
        this.root.remove(b.mesh);
        this.ballPool.push(b.mesh);
        this.fireballs.splice(i, 1);
      }
    }
  }

  /** The nearest fire is HEARD: one positional crackle loop, its gain owned here, gone past 26 m. */
  private updateCrackle(ranked: Fire[]): void {
    const near = ranked.find((f) => f.sound);
    const nd = near ? near.dist : Infinity;
    if (near && nd < 26) {
      if (!this.crackle) this.crackle = sfxLoop('ambient_campfire', 'fire-crackle');
      this.crackle.at({ x: near.x, y: near.y + 0.5, z: near.z });
      this.crackle.set(0.55 * Math.min(1.4, near.size) * Math.min(1, Math.max(0, 1 - (nd - 2) / 24)) ** 1.5, near.kind === 'bonfire' ? 0.85 : 1, 0.3);
    } else if (this.crackle) this.crackle.set(0, 1, 0.6);
  }

  clearAll(): void {
    for (const k of [...this.fires.keys()]) this.remove(k);
    for (const b of this.fireballs) { b.mesh.visible = false; this.root.remove(b.mesh); this.ballPool.push(b.mesh); }
    this.fireballs.length = 0;
    this.anchors.clear();
    if (this.crackle) { stopSfxScope('fire-crackle'); this.crackle = null; }
    this.broker?.parkAll();
  }

  /** Diagnostics (`__fire()`). */
  describe(): Record<string, unknown> {
    return {
      fires: [...this.fires.values()].map((f) => ({ key: f.key, kind: f.kind, size: f.size, rung: f.rung, dist: Math.round(f.dist), light: this.broker?.holds(`fire:${f.key}`) ?? false, flicker: +f.flicker.value.toFixed(3), exposure: +f.A.y.toFixed(3), at: [+f.x.toFixed(1), +f.y.toFixed(2), +f.z.toFixed(1)] })),
      anchors: this.anchors.size,
      fireballs: this.fireballs.length,
      particles: this.particles ? { backend: this.particles.backend, emberCap: this.particles.emberCap, smoke: !!this.particles.smoke, ...this.particles.stats } : null,
      sim: this.sim ? { steps: this.sim.steps } : null,
      noiseReady: fireTextures().noiseReady,
      curve: { physical: this.physical, k: +this.curve.k.toExponential(3), coreScene: +this.curve.coreScene.toFixed(4), coreDisplay: +this.curve.coreDisplay.toFixed(3), heldStops: +this.curve.heldStops.toFixed(2), light1: +this.curve.light1.toFixed(4), smokeLight: this.curve.smoke.map((v) => +v.toFixed(4)) },
      stats: { ...this.stats },
    };
  }

  /**
   * Probe lever: a fire's box projected to the screen (normalised 0..1, y DOWN) — the flame region `[x0, y0, x1, y1]`
   * and the plume region above it — so a probe judges the PICTURE inside the flame, not a counter.
   */
  screenBox(key: string, camera: THREE.Camera): { flame: number[]; plume: number[]; dist: number } | null {
    const f = this.fires.get(key) ?? [...this.fires.values()].find((x) => x.key.endsWith(key));
    if (!f) return null;
    const box = fireBox(f.size);
    const sp = this.app.world.scene.position;
    camera.updateMatrixWorld();
    const proj = (y0: number, y1: number): number[] => {
      let x0 = 1, yy0 = 1, x1 = 0, yy1 = 0;
      for (const dx of [-0.5, 0.5]) for (const dz of [-0.5, 0.5]) for (const dy of [y0, y1]) {
        _v.set(f.x + dx * box.w + sp.x, f.y + dy + sp.y, f.z + dz * box.w + sp.z).project(camera);
        const u = (_v.x + 1) / 2, w = (1 - _v.y) / 2;
        x0 = Math.min(x0, u); x1 = Math.max(x1, u); yy0 = Math.min(yy0, w); yy1 = Math.max(yy1, w);
      }
      return [x0, yy0, x1, yy1].map((v) => +v.toFixed(4));
    };
    const fh = box.h * f.shape.flameH;
    return { flame: proj(0, fh), plume: proj(fh, fh + 3 * f.size), dist: +f.dist.toFixed(1) };
  }

  /**
   * Probe lever: one fire's live shader inputs (the per-object A/B/C, the global soot light) and its children's
   * visibility — the CPU side of a picture, so a black frame can be traced to a uniform before a shader is blamed.
   */
  diag(key: string): Record<string, unknown> | null {
    const f = this.fires.get(key) ?? [...this.fires.values()].find((x) => x.key.endsWith(key));
    if (!f) return null;
    const v4 = (v: THREE.Vector4): number[] => [v.x, v.y, v.z, v.w];
    const sl = uSmokeLight.value as THREE.Vector3;
    return {
      key: f.key, rung: f.rung, dist: f.dist, A: v4(f.A), B: v4(f.B), C: v4(f.C), smokeLight: [sl.x, sl.y, sl.z],
      hazeOn: this.hazeOn, debugHide: this.debugHide, debugNoHaze: this.debugNoHaze,
      children: f.group.children.map((c) => ({ name: c.name, visible: c.visible, material: ((c as THREE.Mesh).material as THREE.Material | undefined)?.name ?? null })),
    };
  }

  /** Probe lever: show / hide one named child of a fire (`fire-volume`, `fire-haze`, `fire-smoke-cards`, `fire-bed` …). */
  debugPart(key: string, name: string, visible: boolean): number {
    const f = this.fires.get(key) ?? [...this.fires.values()].find((x) => x.key.endsWith(key));
    let n = 0;
    if (f) for (const c of f.group.children) if (c.name === name) { c.visible = visible; n++; }
    return n;
  }

  /** The intensities of the broker slots held by fires whose key starts with `prefix` (the town fixtures' probe surface). */
  lightIntensities(prefix = ''): number[] {
    if (!this.broker) return [];
    return this.broker.describe().filter((d) => d.kind === 'source' && d.key?.startsWith(`fire:${prefix}`)).map((d) => d.intensity);
  }

  /**
   * Probe lever: the nearest fire's flicker and its light's intensity, now — with the frame it was written on
   * (`frame`): under a 1-fps software renderer 24 samples in 2 s land on one or two frames, so the probe keeps only
   * samples from DISTINCT frames (session 3: the "both flicker" fail of session 2 was that, not the flame).
   */
  sampleNearest(): { key: string; flicker: number; exposure: number; drawn: number; light: number; frame: number } | null {
    let best: Fire | null = null;
    for (const f of this.fires.values()) if (!best || f.dist < best.dist) best = f;
    // `drawn`: the flame's light AS DRAWN — its exposure × its own emission against its running mean (THE LIGHT FOLLOWS THE FLAME)
    if (!best || !this.broker) return best ? { key: best.key, flicker: best.flicker.value, exposure: best.A.y, drawn: best.A.y * best.emRatio, light: 0, frame: this.frame } : null;
    // The light itself (the broker's describe() rounds to 2 decimals; scene units are small by day).
    return { key: best.key, flicker: best.flicker.value, exposure: best.A.y, drawn: best.A.y * best.emRatio, light: this.broker.intensityOf(`fire:${best.key}`), frame: this.frame };
  }

  dispose(): void {
    this.clearAll();
    this.root.removeFromParent();
  }
}

function fract01(v: number): number { return v - Math.floor(v); }
/** A stable seed from a key (FNV-1a). */
export function hashKey(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0) % 100000;
}

// ── The session's fire system (globalThis-backed: vite's dev-graph duplication must never split it) ──
const store = globalThis as unknown as { __atlasFireSystem?: FireSystem };
export function setFireSystem(s: FireSystem | null): void { store.__atlasFireSystem = s ?? undefined; }
/** The live fire system, or null before the fire module has initialised (owners call again next frame). */
export function fireSystem(): FireSystem | null { return store.__atlasFireSystem ?? null; }

/** Convenience for owners: register a fire if the system is up (returns false when it is not yet). */
export function addFire(key: string, spec: FireSpec): boolean { const s = fireSystem(); if (!s) return false; s.add(key, spec); return true; }
export function removeFire(key: string): void { fireSystem()?.remove(key); }
/** An anchor object for an owner that does not track its fires (null before the system is up — the owner simply draws no flame). */
export function fireAnchor(spec: Omit<FireSpec, 'x' | 'y' | 'z'> & { x?: number; y?: number; z?: number }): THREE.Object3D | null {
  return fireSystem()?.anchor(spec) ?? null;
}
